<template lang="pug">
section#collection-page.section.page-section.top-section
  spinner(v-if="settings.loading")
  div(v-else)
    .collection-header(:style="`background-image:url(${backgroundUrl})`")
      .container
        h1.is-size-1 {{ collection.title }}
        .meta
          .speakers(v-if="speakers.length === 1")
            speaker-tag(:value="speaker")
          .speakers(v-else-if="speakers.length > 1")
            span Multiple speakers
          .sermons {{ sermonLabel }}
          .dates {{ dateLabel }}
        p.collection-desc.is-size-5.has-text-grey-light {{ collection.long_desc | truncate(descLength) }}
    .collection-body
      .container(v-if="isMobile")
        .columns
          .column(
            v-for="episode in episodes"
            :key="episode.id"
          )
            episode-list-item(:value="episode")
      .container(v-else)
        episode-card(
          v-for="episode in episodes"
          :key="episode.id"
          :value="episode"
        )
        //- .columns.is-multiline.is-mobile
        //-   .column.is-half-mobile.is-one-third-desktop.is-one-third-widescreen(
        //-     v-for="episode in episodes"
        //-     :key="episode.id"
        //-   )
        //-     episode-list-item(:value="episode" type="thumbnail")
</template>

<script>
import { mapGetters } from 'vuex';
import cloudinary from 'cloudinary-core';
import moment from 'moment';
import { SEARCH } from '@/utils/http';
import EpisodeCard from '@/components/episodes/EpisodeCard.vue';
import EpisodeListItem from '@/components/episodes/EpisodeListItem.vue';
import ReadMore from '@/components/ReadMore.vue';
import SpeakerTag from '@/components/speakers/SpeakerTag.vue';
import Spinner from '@/components/Spinner.vue';
import * as actionTypes from '@/store/types/actions';
import * as getterTypes from '@/store/types/getters';

export default {
  name: 'collection',
  components: {
    EpisodeCard,
    EpisodeListItem,
    ReadMore,
    SpeakerTag,
    Spinner,
  },
  computed: {
    ...mapGetters({
      account: getterTypes.GET_ACCOUNT,
      collection: getterTypes.GET_EPISODE_COLLECTION,
    }),
    backgroundUrl() {
      if (!this.collection._embedded.artwork) return null;
      let opts = {};
      if (this.isMobile) {
        opts = {
          width: 600,
          height: 600,
          effect: 'blur:700',
          quality: 'auto',
          fetch_format: 'auto',
          crop: 'thumb',
          opacity: 30,
          format: 'png',
        };
      } else {
        opts = {
          width: 2000,
          height: 700,
          effect: 'blur:700',
          quality: 'auto',
          fetch_format: 'auto',
          crop: 'fill',
          opacity: 30,
          format: 'png',
        };
      }
      return this.cloudinary.url(this.collection._embedded.artwork.cl_id, opts);
    },
    dateLabel() {
      const startDate = moment(this.collection.start_date, 'YYYY-MM-DD');
      const endDate = moment(this.collection.end_date, 'YYYY-MM-DD');
      if (startDate.isSame(endDate, 'month')) {
        return startDate.format('MMMM YYYY');
      }
      if (startDate.isSame(endDate, 'year')) {
        return [
          startDate.format('MMMM'),
          endDate.format('MMMM YYYY'),
        ].join(' - ');
      }
      return [
        startDate.format('MMMM YYYY'),
        endDate.format('MMMM YYYY'),
      ].join(' - ');
    },
    descLength() {
      return this.isMobile ? 100 : 250;
    },
    esQuery() {
      const from = this.pagination.per * (this.pagination.page - 1);
      const today = moment().format('YYYY-MM-DD');
      const payload = {
        from: from >= 0 ? from : 0,
        size: this.pagination.per,
        sort: {
          pub_date: { order: 'asc' },
          'title.raw': { order: 'asc' },
        },
        query: {
          bool: {
            must: [
              { match: { account_id: this.account.id } },
              { terms: { collection_ids: [this.$route.params.id] } },
              { match: { is_published: true } },
              { range: { pub_date: { lte: today } } },
            ],
          },
        },
      };
      return payload;
    },
    isMobile() {
      return window.innerWidth <= 769;
    },
    morePages() {
      if (this.pagination.total === 0) return false;
      return Math.ceil(this.pagination.total / this.pagination.per) > this.pagination.page;
    },
    sermonLabel() {
      const count = this.collection.episode_count;
      const label = count === 1 ? 'sermon' : 'sermons';
      return [
        count,
        label,
      ].join(' ');
    },
    speaker() {
      return this.speakers[0];
    },
    speakers() {
      return this.collection._embedded.speakers || [];
    },
  },
  async created() {
    this.cloudinary = cloudinary.Cloudinary.new({
      cloud_name: process.env.VUE_APP_CLOUDINARY_CLOUD_NAME,
    });
    if (this.$route.params.id) {
      this.settings.loading = true;
      const payload = {
        id: this.$route.params.id,
        zoom: 'artwork,speakers',
      };
      await this.$store.dispatch(actionTypes.DO_GET_EPISODE_COLLECTION, payload);
      await this.getPage(1);
      this.settings.loading = false;
    }
    return Promise.resolve();
  },
  data() {
    return {
      cloudinary: null,
      bottom: false,
      episodes: [],
      pagination: {
        page: 1,
        per: 12,
        total: 0,
      },
      settings: {
        loading: false,
      },
    };
  },
  destroyed() {
    window.removeEventListener('scroll', this.scrollHandler);
  },
  methods: {
    async getPage(page) {
      this.pagination.page = page;
      const { data } = await SEARCH.post('/doxa_episodes/_search', this.esQuery);
      this.pagination.total = data.hits.total;
      if (data.hits.total > 0) {
        const hits = data.hits.hits.map(h => h._source);
        this.episodes = this.episodes.concat(hits);
        return data;
      }
      return Promise.resolve();
    },
    scrollHandler() {
      const y = window.scrollY;
      const visible = document.documentElement.clientHeight;
      const pageHeight = document.documentElement.scrollHeight;
      const adjustedHeight = pageHeight - 300;
      const bottomOfPage = visible + y >= adjustedHeight;
      this.bottom = bottomOfPage || adjustedHeight < visible;
    },
  },
  mounted() {
    window.addEventListener('scroll', this.scrollHandler);
  },
  watch: {
    bottom(val) {
      if (val && this.morePages) this.getPage(this.pagination.page + 1);
    },
  },
};
</script>

<style lang="scss">
#collection-page {
  .collection-header {
    @include hero;
  }
  .collection-body {
    margin: 3rem 0;
  }
  @media (max-width: $tablet) {
    .collection-header {
      .meta {
        .speakers {
          flex-basis: 100%;
          margin-bottom: 0.65rem;
          .speaker-tag {
            justify-content: center;
          }
        }
      }
    }
    .collection-body {
      margin: 1.75rem 0;
      .episode-list-item {
        .card-content {
          border-left: 0;
          border-right: 0;
        }
      }
    }
  }
}
</style>
